import { Checkbox, Chip, Switch } from "@heroui/react";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { TriangleAlert } from "lucide-react";

import EditableTextCell from "@/components/common/EditableTextCell";
import HintTooltip from "@/components/common/HintTooltip";
import LinkCell from "@/components/common/LinkCell";
import type { SingleFieldKind } from "@/components/common/SingleFieldEditDialog";
import "@/components/table/columnMeta";

import type { Project, ProjectInput } from "./api";

/**
 * 案件一覧の列定義。**ヘッダとセルの単一ソース**。
 *
 * 見積一覧と違い金額は無い。案件が追うのは「何をいつまでに何台作るか」なので、
 * 納期と出荷が主役になる。
 *
 * 直せる項目は**クリックで 1 項目だけ編集するセル**（[[EditableTextCell]]）に
 * してある。編集ダイアログの開閉は一覧側が持つ（セルごとに Modal を持たせない）。
 * 取引先一覧（[[buildPartnerColumns]]）と同じ作り。
 *
 * **直せない列もある**。
 *
 * - **工番** … 詳細へのリンク。詳細画面でも直せるようにしていない
 * - **出荷** … 専用の API（と確認ダイアログ）で記録する。一覧のセルを押して
 *   切り替わると、流し見の最中に出荷済みにしてしまう
 * - **見積 / 明細** … 明細の紐付けから導く値で、案件が直接は持たない
 *
 * **「見積未紐付」の列は置いていない**。見積明細に紐付いていない案件明細の数を
 * 出していたが、数字だけでは「見積の後付け漏れ」と「もともと見積に無い行」
 * （手で足した行、受注後の追図から起こした行）の区別が付かず、正常な案件でも
 * 警告色が立ち続けていた。同じ数は案件詳細の明細タブに出ているので、そちらで
 * 行ごとに見てもらう。
 */
const helper = createColumnHelper<Project>();

/**
 * クリックで編集できる項目。編集ダイアログの設定もここに持つ。
 *
 * **見せる値と直す値が違うことがある**（得意先は名前を見せて ID を直す）ので、
 * ダイアログに渡す値は `value` で別に取る。
 */
export interface ProjectField {
  /** 編集ダイアログの見出しに出す項目名。 */
  label: string;
  kind?: SingleFieldKind;
  /** 編集ダイアログの補足。 */
  description?: string;
  /** ダイアログの初期値（見せている文字列とは限らない）。 */
  value: (project: Project) => string | null;
  /** 入力された値を API に送る形にする。 */
  toInput: (value: string | null) => ProjectInput;
  /**
   * 選択肢が要る項目。一覧側が引いた一覧を入れる（列定義は API を叩かない）。
   * 得意先と直送先は同じ取引先マスタだが、用途で絞った別々の一覧を出す。
   */
  options?: "partners" | "shipTos";
}

const FIELDS = {
  project_title: {
    label: "案件名",
    value: (p) => p.project_title,
    toInput: (v) => ({ project_title: v }),
  },
  client: {
    label: "得意先",
    kind: "select",
    options: "partners",
    value: (p) => (p.client_id == null ? null : String(p.client_id)),
    toInput: (v) => ({ client_id: v == null ? null : Number(v) }),
  },
  ship_to: {
    label: "直送先",
    kind: "select",
    options: "shipTos",
    description:
      "得意先とは別の場所へ納めるときに選びます。選択肢は取引先マスタで用途に「直送先」が入っているものです。空欄なら直送しません。",
    value: (p) => (p.ship_to_id == null ? null : String(p.ship_to_id)),
    toInput: (v) => ({ ship_to_id: v == null ? null : Number(v) }),
  },
  client_po_number: {
    label: "客先注文番号",
    value: (p) => p.client_po_number,
    toInput: (v) => ({ client_po_number: v }),
  },
  set_count: {
    label: "セット数",
    kind: "number",
    description:
      "何セット作るか。入れるとセット品になります。明細の数量は「1 セットあたり × セット数」の合算です。",
    value: (p) => (p.set_count == null ? null : String(p.set_count)),
    toInput: (v) => ({ set_count: v == null || v === "" ? null : Number(v) }),
  },
  order_date: {
    label: "受注日",
    kind: "date",
    value: (p) => p.order_date,
    toInput: (v) => ({ order_date: v }),
  },
  deadline: {
    label: "納期",
    kind: "date",
    description: "案件全体の納期です。行ごとに違う場合は明細の納期で持ちます。",
    value: (p) => p.deadline,
    toInput: (v) => ({ deadline: v }),
  },
  shipping_planned_date: {
    label: "出荷予定日",
    kind: "date",
    value: (p) => p.shipping_planned_date,
    toInput: (v) => ({ shipping_planned_date: v }),
  },
  note: {
    label: "備考",
    kind: "textarea",
    value: (p) => p.note,
    toInput: (v) => ({ note: v }),
  },
} satisfies Record<string, ProjectField>;

/** 今日（ローカル）を YYYY-MM-DD で返す。納期超過の判定に使う。 */
function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
}

export interface ProjectColumnHandlers {
  canWrite: boolean;
  /** まとめて削除する対象として選ばれている案件の id。 */
  selectedIds: Set<number>;
  onToggleSelect: (project: Project) => void;
  onEditField: (project: Project, field: ProjectField) => void;
  /** セット品の印を切り替える（スイッチなのでダイアログを通さない）。 */
  onToggleSetProduct: (project: Project) => void;
}

export function buildProjectColumns({
  canWrite,
  selectedIds,
  onToggleSelect,
  onEditField,
  onToggleSetProduct,
}: ProjectColumnHandlers): ColumnDef<Project, never>[] {
  /** 直せる項目のセル。見せる文字列は列ごとに渡す（値と表示が違うことがある）。 */
  const editable =
    (field: ProjectField, tone?: "danger") =>
    (project: Project, display: string | null) => (
      <EditableTextCell
        label={field.label}
        value={display}
        canWrite={canWrite}
        tone={tone}
        onEdit={() => onEditField(project, field)}
      />
    );

  const dataColumns = [
    helper.accessor("project_number", {
      id: "project_number",
      header: "工番",
      size: 140,
      meta: {
        label: "工番",
        mandatory: true,
        pinned: true,
        sortKey: "project_number",
      },
      // 見積が 1 つも無い案件は工番の右に印を付ける。特急で先に走った案件は
      // あとから見積を起こす約束（[[urgent-project-flow]]）なので、**起こし忘れ
      // が一覧で目に入る**必要がある。確定した見積（source）も作りかけ（raised）も
      // 無いときだけ出す。作っている途中の案件に警告を出すと、出たままになる。
      cell: (ctx) => {
        const project = ctx.row.original;
        const withoutQuotation =
          project.source_quotation_id == null && project.raised_quotation_id == null;
        return (
          <div className="flex items-center gap-1">
            <LinkCell
              to={`/projects/${project.project_id}`}
              label={`案件 ${ctx.getValue()} を開く`}
              bold
            >
              {ctx.getValue()}
            </LinkCell>
            {withoutQuotation && (
              <HintTooltip content="見積がない案件です。">
                {/* span で包む。ツールチップは hover と focus で開くので、
                    キーボードでも読めるように当たり判定を持つ要素にする。 */}
                <span
                  tabIndex={0}
                  role="img"
                  // 色と形だけでは伝わらないので、読み上げにも理由を乗せる
                  // （見積一覧の「受注済」の印と同じ作り）。
                  aria-label="見積がない案件です"
                  className="inline-flex shrink-0 rounded outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  <TriangleAlert size={16} className="text-warning-500" aria-hidden />
                </span>
              </HintTooltip>
            )}
          </div>
        );
      },
    }),
    helper.accessor("shipped_at", {
      id: "shipped_at",
      header: "出荷",
      size: 110,
      meta: { label: "出荷", sortKey: "shipped_at" },
      // 出荷は専用の API で記録する（詳細画面の確認ダイアログを通す）。一覧の
      // セルを押して切り替わると、流し見の最中に出荷済みにしてしまう。
      cell: (ctx) => (
        <Chip size="sm" variant="flat" color={ctx.getValue() ? "success" : "default"}>
          {ctx.getValue() ? "出荷済" : "未出荷"}
        </Chip>
      ),
    }),
    helper.accessor("project_title", {
      id: "project_title",
      header: "案件名",
      size: 240,
      meta: { label: "案件名", sortKey: "project_title" },
      cell: (ctx) => editable(FIELDS.project_title)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("client_name", {
      id: "client_name",
      header: "得意先",
      size: 200,
      meta: { label: "得意先" },
      // 見せるのは名前、直すのは ID。
      cell: (ctx) => editable(FIELDS.client)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("ship_to_name", {
      id: "ship_to_name",
      header: "直送先",
      size: 200,
      meta: { label: "直送先" },
      // 見せるのは名前、直すのは ID（得意先と同じ）。
      cell: (ctx) => editable(FIELDS.ship_to)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("client_po_number", {
      id: "client_po_number",
      header: "客先注文番号",
      size: 160,
      meta: { label: "客先注文番号" },
      cell: (ctx) =>
        editable(FIELDS.client_po_number)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("is_set_product", {
      id: "is_set_product",
      header: "セット品",
      size: 100,
      meta: { label: "セット品" },
      // セット品かどうかは**明細の数量の読み方が変わる**（畳んだ明細の数量は
      // 購番をまたいだ合算）。1 項目しかない印なので、ダイアログを通さず
      // スイッチで切り替える。**外すとセット数も消える**ので、消えたことは
      // 一覧側がその場で知らせる。
      cell: (ctx) => {
        const project = ctx.row.original;
        return (
          <Switch
            size="sm"
            isSelected={project.is_set_product}
            isDisabled={!canWrite}
            onValueChange={() => onToggleSetProduct(project)}
            aria-label={`案件 ${project.project_number} のセット品`}
            classNames={{ base: "m-0 p-0", label: "hidden" }}
          />
        );
      },
    }),
    helper.accessor("set_count", {
      id: "set_count",
      header: "セット数",
      size: 90,
      meta: { label: "セット数", numeric: true },
      // 客先購番が付いている案件のセット数は**購番の本数から決まる導出値**なので
      // 直せない（API も 409 で断る）。紙の無い案件だけ手で入れられる。
      cell: (ctx) => {
        const project = ctx.row.original;
        const display =
          project.set_count == null ? null : `${project.set_count} セット`;
        if (project.client_order_numbers.length > 0) {
          return display == null ? (
            <span className="text-default-400">—</span>
          ) : (
            <span>{display}</span>
          );
        }
        return editable(FIELDS.set_count)(project, display);
      },
    }),
    helper.accessor("client_order_numbers", {
      id: "client_order_numbers",
      header: "客先購番",
      size: 220,
      meta: { label: "客先購番" },
      // 畳む前の購番（[[client_order_lines]]）。**直せない**。紙の行そのものの
      // 記録で、取り込みで決まる値なので画面から書き換えさせない。
      // 検索はこの値も見るので、ここに出ていれば「なぜ引けたか」が分かる。
      // 値の型は列定義全体で never に揃えているので、行から取り出す。
      cell: (ctx) => {
        const numbers = ctx.row.original.client_order_numbers;
        if (numbers.length === 0) return <span className="text-default-400">—</span>;
        return <span className="tabular-nums">{numbers.join(" / ")}</span>;
      },
    }),
    helper.accessor("source_quotation_number", {
      id: "source_quotation",
      header: "見積",
      size: 140,
      meta: { label: "起票元の見積" },
      // 起票元の見積。**いくらで受けたのか**を辿る入口で、一覧から直接移れる
      // ようにする。繋がりは明細の紐付けから導くので、ここでは直せない
      // （手で作った案件には起票元が無く「—」になる）。
      cell: (ctx) => {
        const project = ctx.row.original;
        if (project.source_quotation_id == null) {
          return <span className="text-default-400">—</span>;
        }
        return (
          <LinkCell
            to={`/quotations/${project.source_quotation_id}`}
            label={`見積 ${project.source_quotation_number} を開く`}
          >
            {project.source_quotation_number}
          </LinkCell>
        );
      },
    }),
    helper.accessor("order_date", {
      id: "order_date",
      header: "受注日",
      size: 120,
      meta: { label: "受注日", sortKey: "order_date" },
      cell: (ctx) => editable(FIELDS.order_date)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("deadline", {
      id: "deadline",
      header: "納期",
      size: 120,
      meta: { label: "納期", sortKey: "deadline" },
      cell: (ctx) => {
        const project = ctx.row.original;
        // 未出荷のまま納期を過ぎたものは赤で示す（出荷済みなら意味が無い）。
        // 編集できるようにしても色の意味が消えないよう、赤いまま押せる値にする。
        const overdue =
          project.deadline != null &&
          project.deadline < today() &&
          project.shipped_at == null;
        return editable(FIELDS.deadline, overdue ? "danger" : undefined)(
          project,
          project.deadline,
        );
      },
    }),
    helper.accessor("shipping_planned_date", {
      id: "shipping_planned_date",
      header: "出荷予定日",
      size: 130,
      meta: { label: "出荷予定日", sortKey: "shipping_planned_date" },
      cell: (ctx) =>
        editable(FIELDS.shipping_planned_date)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("item_count", {
      id: "item_count",
      header: "明細",
      size: 80,
      meta: { label: "明細行数", numeric: true },
      cell: (ctx) => ctx.getValue(),
    }),
    helper.accessor("note", {
      id: "note",
      header: "備考",
      size: 200,
      meta: { label: "備考" },
      cell: (ctx) => editable(FIELDS.note)(ctx.row.original, ctx.getValue()),
    }),
  ];

  // 選択列（まとめて削除するためのチェックボックス）は**書き込める人にだけ**
  // 出す。選ぶ目的が削除なので、押せない人には場所を取らせない。全行の選択は
  // 表の上の「このページをすべて選択」で行う（見積一覧と同じ作り）。
  if (!canWrite) return dataColumns as ColumnDef<Project, never>[];
  return [
    helper.display({
      id: "select",
      header: "選択",
      size: 60,
      enableResizing: false,
      meta: { label: "選択", mandatory: true, pinned: true },
      cell: (ctx) => {
        const project = ctx.row.original;
        return (
          <Checkbox
            size="sm"
            isSelected={selectedIds.has(project.project_id)}
            onValueChange={() => onToggleSelect(project)}
            aria-label={`案件 ${project.project_number} を選択`}
            classNames={{ base: "m-0 p-0", label: "hidden" }}
          />
        );
      },
    }),
    ...dataColumns,
  ] as ColumnDef<Project, never>[];
}

/** 列 id の一覧（設定の永続化で「未知のキー」を捨てるのに使う）。 */
export const projectColumnIds = [
  "select",
  "project_number",
  "shipped_at",
  "project_title",
  "client_name",
  "ship_to_name",
  "client_po_number",
  "is_set_product",
  "set_count",
  "client_order_numbers",
  "source_quotation",
  "order_date",
  "deadline",
  "shipping_planned_date",
  "item_count",
  "note",
];

/**
 * 既定では非表示にする列。
 *
 * 購番は**セット品のときだけ複数本並ぶ**値で、1 件の注文なら客先注文番号と同じ
 * 値が出るだけなので常に出すと表が横に伸びる。購番で引く場面（客先からの
 * 問い合わせ）では検索が購番も見るので、列を出していなくても案件は見つかる。
 */
export const projectDefaultHiddenColumnIds = ["client_order_numbers"];

/** 左に固定する列の id。 */
export const projectPinnedColumnIds = ["select", "project_number"];
